/**
 * The alert stack, placed by its caller: filter chips, the ⚙ per-kind mute
 * list, tiered entries (pinned attacks, warnings, good news, folded info) with their
 * actions, and the collapsible "Notifications N" pill. Reads everything from
 * `useAlerts` (useAlerts.ts); a HUD layout puts it wherever its notification
 * slot is (the caller sets position and width). The caller's container must
 * be a height-bounded flex column, or the entry list can't scroll.
 */

import { useState } from 'react';
import type { Cell } from '@/game/sim';
import {
  ALERT_GROUPS,
  ALERT_KINDS,
  type AlertGroup,
  countChip,
  idleWorkerIds,
  type Notification,
  type NotificationKind,
  stackView,
  toggleMuted,
} from '@/game/view/notifications';
import type { SendTarget } from '@/game/view/sendTroops';
import { useSimFrame } from './app/simFrame';
import { CollapseToggle, usePersisted } from './persisted';
import { type Alerts, alertFocus } from './useAlerts';

export interface AlertStackProps {
  alerts: Alerts;
  onFocus: (pos: Cell) => void;
  /** Select an own city and go there (idle-worker notifications). */
  onSelectCity: (id: string) => void;
  /** Select exactly these own units (a city's idle workers); the stack pans there. */
  onSelectUnits?: (ids: string[]) => void;
  /** Open the Send troops picker for a found rival or an attacker; omitted while spectating. */
  onSendTroops?: (target: SendTarget) => void;
  /** Open the Upgrades dock (the city era alert's Upgrades button); omitted while spectating. */
  onOpenUpgrades?: () => void;
}

export function AlertStack({
  alerts,
  onFocus,
  onSelectCity,
  onSelectUnits,
  onSendTroops,
  onOpenUpgrades,
}: AlertStackProps) {
  const frame = useSimFrame();
  const { playerId } = frame;
  const state = frame.view;
  const { notifications, muted, setMutedList, dismissEntry } = alerts;
  const [collapsed, setCollapsed] = usePersisted('notificationsCollapsed', false);
  // Older losses past MAX_FEED_LOSSES are folded into one row until expanded.
  const [allLosses, setAllLosses] = useState(false);
  // Filter chip and the mute list's panel.
  const [group, setGroup] = useState<AlertGroup | null>(null);
  const [muteOpen, setMuteOpen] = useState(false);
  // Tiers: attacks pinned on top, then warnings, good news, then folded info; newest first within each.
  const stack = stackView(notifications, state.elapsed, muted, {
    group: group ?? undefined,
    allLosses,
  });
  const focus = (n: Notification) => alertFocus(state, n, onFocus, onSelectCity);
  // Selects the city's idle workers (not the city) and goes there.
  const selectIdle = (n: Notification) => {
    const ids = n.cityId ? idleWorkerIds(state, frame.ctx, playerId).get(n.cityId) : undefined;
    if (!ids?.length) return focus(n);
    onSelectUnits?.(ids);
    if (n.pos) onFocus(n.pos);
  };

  return (
    <div className="pointer-events-none flex min-h-0 flex-col gap-1.5 text-xs">
      {!collapsed && (stack.total > 0 || muted.size > 0) && (
        <div className="pointer-events-auto flex min-h-0 flex-col gap-1.5">
          <div className="flex shrink-0 items-center gap-1 px-0.5">
            <div className="flex min-w-0 flex-1 flex-wrap gap-1">
              {stack.total > 0 && (
                <button
                  type="button"
                  aria-label={`All notifications, ${stack.total}`}
                  aria-pressed={stack.group === null}
                  onClick={() => setGroup(null)}
                  className={`chip ${stack.group === null ? 'chip-selected' : ''}`}
                >
                  All {stack.total}
                </button>
              )}
              {ALERT_GROUPS.filter((g) => stack.counts[g.id] > 0).map((g) => (
                <button
                  key={g.id}
                  type="button"
                  title={g.label}
                  aria-label={`${g.label}, ${stack.counts[g.id]}`}
                  aria-pressed={stack.group === g.id}
                  onClick={() => setGroup(stack.group === g.id ? null : g.id)}
                  className={`chip ${stack.group === g.id ? 'chip-selected' : ''}`}
                >
                  <span aria-hidden="true">{g.icon}</span> {stack.counts[g.id]}
                </button>
              ))}
            </div>
            <button
              type="button"
              aria-label="Mute alert kinds"
              aria-expanded={muteOpen}
              title="Mute alert kinds"
              onClick={() => setMuteOpen(!muteOpen)}
              className={`btn btn-ghost btn-icon h-6 w-6 shrink-0 text-sm ${muteOpen ? 'chip-selected' : ''}`}
            >
              ⚙
            </button>
          </div>
          {muteOpen && (
            <div className="panel flex shrink-0 flex-col gap-0.5 rounded-xl px-3 py-2">
              <span className="text-fg-muted">Show these alerts</span>
              {(Object.keys(ALERT_KINDS) as NotificationKind[])
                .filter((k) => ALERT_KINDS[k].mutable)
                .map((k) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 py-0.5">
                    <input
                      type="checkbox"
                      checked={!muted.has(k)}
                      onChange={() => setMutedList(toggleMuted([...muted], k))}
                    />
                    <span aria-hidden="true">{ALERT_KINDS[k].icon}</span>
                    <span>{ALERT_KINDS[k].label}</span>
                  </label>
                ))}
              <span className="text-fg-subtle">Attacks and losses always show.</span>
            </div>
          )}
          <div className="flex min-h-0 flex-col gap-1.5 overflow-y-auto">
            {stack.tiers.map(({ tier, entries }) => (
              <div key={tier} className="flex shrink-0 flex-col gap-1.5">
                {tier === 'info' && stack.tiers.length > 1 && (
                  <span className="px-1 text-fg-subtle">Earlier</span>
                )}
                {entries.map(({ n, pinned, ongoing: hit }) =>
                  tier === 'info' ? (
                    <FoldedRow
                      key={n.id}
                      n={n}
                      onFocus={() => focus(n)}
                      onSendTroops={onSendTroops}
                      onDismiss={() => dismissEntry(n.id)}
                    />
                  ) : (
                    <AlertCard
                      key={n.id}
                      n={n}
                      pinned={pinned}
                      ongoing={hit}
                      onFocus={() => focus(n)}
                      onSendTroops={onSendTroops}
                      onOpenUpgrades={onOpenUpgrades}
                      onSelectIdle={onSelectUnits ? () => selectIdle(n) : undefined}
                      onDismiss={() => dismissEntry(n.id)}
                    />
                  )
                )}
              </div>
            ))}
            {stack.olderLosses > 0 || allLosses ? (
              <button
                type="button"
                onClick={() => setAllLosses(!allLosses)}
                className="pill shrink-0 py-1 text-center text-fg-muted hover:text-fg"
              >
                {allLosses ? 'Hide older losses' : `+${stack.olderLosses} older losses`}
              </button>
            ) : null}
          </div>
        </div>
      )}
      {stack.total > 0 && (
        <div className="pill pointer-events-auto flex shrink-0 items-center justify-between py-0.5 pl-3 pr-1">
          <span className="text-fg-muted">
            Notifications <span className="text-fg-subtle">{stack.total}</span>
          </span>
          <CollapseToggle
            collapsed={collapsed}
            onToggle={() => setCollapsed(!collapsed)}
            label="notifications"
          />
        </div>
      )}
    </div>
  );
}

/** "×3" badge for an entry that merged several events. */
function CountChip({ n }: { n: Notification }) {
  const chip = countChip(n);
  return chip ? <span className="chip tnum shrink-0">{chip}</span> : null;
}

/**
 * Attack and warning entries as cards: a pinned attack (still being fought)
 * has no ✕ and pulses while hits keep coming; its action row (Go, Send troops, Select idle workers)
 * fits the kind.
 */
function AlertCard({
  n,
  pinned,
  ongoing,
  onFocus,
  onSendTroops,
  onSelectIdle,
  onOpenUpgrades,
  onDismiss,
}: {
  n: Notification;
  pinned: boolean;
  ongoing: boolean;
  onFocus: () => void;
  onSendTroops?: (target: SendTarget) => void;
  onSelectIdle?: () => void;
  onOpenUpgrades?: () => void;
  onDismiss: () => void;
}) {
  const upgrades = n.kind === 'era' ? onOpenUpgrades : undefined;
  const goable = !!(n.pos || n.cityId);
  const target = onSendTroops ? n.target : undefined;
  const selectIdle = n.kind === 'idle' && n.cityId ? onSelectIdle : undefined;
  const open = n.action && n.cityId ? n.action : undefined;
  return (
    <div
      className={`panel flex shrink-0 flex-col rounded-xl ${ALERT_KINDS[n.kind].style} ${ongoing ? 'alert-ring' : ''}`}
    >
      <div className="flex items-start">
        <div
          className={`flex min-w-0 flex-1 items-start gap-2 py-2 pl-3 text-left ${pinned ? 'pr-3' : ''}`}
        >
          <span aria-hidden="true">{ALERT_KINDS[n.kind].icon}</span>
          <span className="min-w-0 flex-1">
            {n.text}
            {n.detail ? <span className="block text-fg-muted">{n.detail}</span> : null}
            {n.attackers ? <span className="block text-fg-muted">by {n.attackers}</span> : null}
            {n.progress !== undefined ? (
              <span
                role="progressbar"
                aria-valuenow={Math.round(n.progress * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
                className="mt-1.5 block h-1 overflow-hidden rounded-full bg-fill"
              >
                <span
                  className="block h-full bg-danger"
                  style={{ width: `${Math.round(n.progress * 100)}%` }}
                />
              </span>
            ) : null}
          </span>
          <CountChip n={n} />
          {n.badge && <span className="chip tnum shrink-0 text-warn">{n.badge}</span>}
          {pinned && (
            <span
              role="img"
              aria-label="Pinned"
              title="Pinned until the fighting stops"
              className="shrink-0"
            >
              📌
            </span>
          )}
        </div>
        {!pinned && <DismissButton onClick={onDismiss} />}
      </div>
      {(goable || target || selectIdle || open || upgrades) && (
        <div className="flex flex-wrap justify-end gap-1.5 px-2 pb-1.5">
          {selectIdle && (
            <button
              type="button"
              onClick={selectIdle}
              className="btn btn-primary h-6 shrink-0 px-2 text-[11px]"
            >
              Select idle workers
            </button>
          )}
          {open && (
            <button
              type="button"
              onClick={onFocus}
              className="btn btn-primary h-6 shrink-0 px-2 text-[11px]"
            >
              {open}
            </button>
          )}
          {goable && !open && (
            <button
              type="button"
              title="Go there"
              onClick={onFocus}
              className="btn btn-secondary h-6 shrink-0 px-2 text-[11px]"
            >
              Go ⌖
            </button>
          )}
          {upgrades && (
            <button
              type="button"
              onClick={upgrades}
              className="btn btn-secondary h-6 shrink-0 px-2 text-[11px]"
            >
              Upgrades
            </button>
          )}
          {target && (
            <button
              type="button"
              title={`Send troops at ${target.label}`}
              onClick={() => onSendTroops?.(target)}
              className="btn btn-secondary h-6 shrink-0 px-2 text-[11px]"
            >
              Send troops
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Info entries as one folded line each: icon, text, count, ⌖, Send troops, ✕. */
function FoldedRow({
  n,
  onFocus,
  onSendTroops,
  onDismiss,
}: {
  n: Notification;
  onFocus: () => void;
  onSendTroops?: (target: SendTarget) => void;
  onDismiss: () => void;
}) {
  const target = onSendTroops ? n.target : undefined;
  return (
    <div
      className={`panel flex shrink-0 items-center gap-1.5 rounded-xl py-1 pl-2.5 ${ALERT_KINDS[n.kind].style}`}
    >
      <span aria-hidden="true">{ALERT_KINDS[n.kind].icon}</span>
      <span className="min-w-0 flex-1">{n.text}</span>
      <CountChip n={n} />
      {(n.pos || n.cityId) && (
        <button
          type="button"
          aria-label="Go there"
          title="Go there"
          onClick={onFocus}
          className="btn btn-ghost btn-icon h-6 w-6 shrink-0 text-xs"
        >
          ⌖
        </button>
      )}
      {target && (
        <button
          type="button"
          title={`Send troops at ${target.label}`}
          onClick={() => onSendTroops?.(target)}
          className="btn btn-secondary h-6 shrink-0 px-2 text-[11px]"
        >
          Send troops
        </button>
      )}
      <DismissButton onClick={onDismiss} />
    </div>
  );
}

export function DismissButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Dismiss notification"
      title="Dismiss"
      onClick={onClick}
      className="px-2.5 py-1.5 text-fg-subtle transition hover:text-fg"
    >
      ✕
    </button>
  );
}
